/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AutocompleteItemDto, ID } from '@gh/common-shared-data';
import { createEnum, EnumSet, WithOptimisticLock, WithPrivilegedToken } from '@gh/core-data';
import { SystemVarsAccountsXeroSyncProgress } from '../../builder-setup/system-vars/resources/model/system-vars-accounts.model';

import { AccountingAccountDto } from '../accounting-accounts';
import {
  ContactTermsPayables,
  ContactTermsReceivables,
} from '../contacts/model/contact-details-shared.dto';
import { ContactListItemDto } from '../contacts/model/contact-list-item.dto';
import { CostCenterFinderListItemDto } from '../cost-centers/model/cost-center-list-item.dto';
import { MailRequirementSetting } from './mail-requirement-setting.enum';

export type SystemVarsFranchiseeDto = WithOptimisticLock<WithPrivilegedToken<{
  id: Maybe<ID>;
  email: string;
  formedDate: Date;
  companyName: string;
  displayName: string;
  usersFromOtherOfficesAccess: boolean;
  isUsersFromOtherOfficesAccessActive: boolean;
  /** Independent gate for legacy email multi-office (Send Emails from). */
  usersEmailAccessFromOtherOffices?: boolean;
  isUsersEmailAccessFromOtherOfficesActive?: boolean;
  typeOfBusiness?: string;
  systemName?: string;
  taxId?: ID;
  tradingAs?: string;
  abn?: string;
  gst?: string;
  acn?: string;
  street1?: string;
  city?: string;
  state?: string;
  postCode?: string;
  street2?: string;
  licenseNo?: string;
  phoneOffice?: string;
  fax?: string;
  phoneMobile?: string;
  publishToWeb: boolean;
  isPublishToWebAvailable: boolean;
  fee?: number;
  hasFeeUpdatePermission?: boolean;
  billing: {
    after36Months: number;
    assignmentFee: number;
    contractSignedDate: Date;
    currentAgreement: Date;
    depositDate: Date;
    franchiseFee: number;
    initialFranchiseFee: number;
    officeClosed: Date;
    onSellDate: Date;
    originalCommencement: Date;
    renewalDate: Date;
    saleAmount: number;
    taxRate: number;
    term: number;
    within18Months: number;
    within24Months: number;
    within36Months: number;
  };
}>>;

export type SystemVarsContactsDto = WithOptimisticLock<{
  onNotEnteredForContact: MailRequirementSetting;
  defaultDeliveryToEmail: boolean;
}>;

export type SystemVarsMasterZoneDetailsDto = WithOptimisticLock<{
  id: ID;
  code: string;
  name: string;
  active?: boolean;
  companyName?: string;
  businessType?: string;
  formedDate?: Date;
  taxId?: ID;
  acn?: number;
  abn?: number;
  gst?: number;
  street1?: string;
  tradingAs?: string;
  street2?: string;
  city?: string;
  state?: string;
  postcode?: number;
  phone1?: string;
  phone2?: string;
  fax?: string;
}>;

export type SystemVarsAccountsTwilioDto = WithOptimisticLock<{
  twilioPhoneNumber: string;
  twilioWebhook: string;
  twilioWebhookUrlPhoneCalls: string;
}>;

export const JobContractStartBasedOnType = createEnum('CONTRACT_START_BASED_ON', {
  ScheduleStart: 'SCHEDULE_START',
  NominatedTasks: 'NOMINATED_TASKS',
});

export type JobContractStartBasedOnType = EnumSet<typeof JobContractStartBasedOnType>;

export const JobContractDaysPeriodType = createEnum('CONSTRUCTION_PERIOD_INCLUDES', {
  AllDays: 'ALL_DAYS',
  WorkingDays: 'WORKING_DAYS',
});

export type JobContractDaysPeriodType = EnumSet<typeof JobContractDaysPeriodType>;

export type SystemVarsJobDto = WithPrivilegedToken<WithOptimisticLock<{
  jobLettersAvailable: string[];
  jobLettersAvailableVersion: number;
  dppAllowed: boolean;
  jobShowFinalisedJobsByDefault: boolean;
  jobColorSelectionMemo: string;
  jobCsCostCenterClone: boolean;
  jobCsCostCenterRequired: boolean;
  jobUseCodeOfCompliance: boolean;
  jobDelayDays: number;
  jobContractStartBasedOn: JobContractStartBasedOnType;
  jobContractConstructionPeriodIncludes: JobContractDaysPeriodType;
  jobChangeOrdersParagraph: string;
  jobTimeExtension: number;
  jobSupplierFooter: string;
  jobCustomerFooter: string;
  jobNumberOfMonthsAutoFinal3iseJob: string;
  jobNumberOfMonthsMakeDppNotProceeding: string;
  jobUseCouncilPermitDate: boolean;
  useLetterhead: boolean;
  jobCostCenter1?: AutocompleteItemDto;
  jobCostCenter2?: AutocompleteItemDto;
  jobCostCenter3?: AutocompleteItemDto;
  jobCostCenter4?: AutocompleteItemDto;
}>>;

export type ExcludedCcRangeDto = {
  from: AutocompleteItemDto;
  to: AutocompleteItemDto;
};

export type SystemVarsReceivablesBaseDto = WithOptimisticLock<{
  recDefaultPaymentDue: ContactTermsReceivables;
  recDefaultPaymentBalanceDays: number;
  recDefaultPaymentDiscountDays: number;
  recDefaultPaymentDiscountPct: number;
  latePaymentTerms: string;
  showBankDetails: boolean;
  drawLetterRequired: boolean;
  drawStatementRequired: boolean;
  escrowAllowed: boolean;
  lenderScheduleAllowed: boolean;
  excludedCostCenters: ExcludedCcRangeDto[];
  bsbNumber?: number;
  bankName?: string;
  bankBranchname?: string;
  accountNumber?: string;
  accountName?: string;
}>;

export type SystemVarsReceivablesDto = SystemVarsReceivablesBaseDto & {
  excludedCostCenters: ExcludedCcRangeDto[];
};

export const PolicyReportingMethod = createEnum('POLICY_REPORTING_METHOD', {
  Fixed12Month: 1,
  Rolling12Month: 2,
});

export type PolicyReportingMethod = EnumSet<typeof PolicyReportingMethod>;

export type SystemVarsManagerDto = WithOptimisticLock<{
  useFacility: boolean;
  salesProfitPct: number;
  excludeVariations: boolean;
  policyNumber: string;
  policyStartDate: Date;
  policyReportingMethod: PolicyReportingMethod;
  policyAnnualAmount: number;
  policyUncompleteAmount: number;
  policyNumberOfPolicies: number;
  company?: ContactListItemDto;
  commissionCc?: CostCenterFinderListItemDto;
  interestCc?: CostCenterFinderListItemDto;
}>;

export type SystemVarsManagerStatisticDto = {
  availableAnnual: number;
  availableUncomplete: number;
  availablePoliciesCount: number;
  jobsAnnual: number;
  jobsCount: number;
  jobsUncomplete: number;
};

export type SystemVarsSupervisorDto = WithOptimisticLock<{
  calendarWarningRequired: boolean;
  calendarWarningDays: number;
  autoEmailChangeOrders: boolean;
  estimatorSeeSupervisorReports: boolean;
  supervisorReportsIncludeUnsentOrders: boolean;
}>;

export type SystemVarsPayablesDto = WithOptimisticLock<{
  contractLicnApplicable: boolean;
  trackingCaptions: string[];
  payDefaultPaymentDue: ContactTermsPayables;
  payDefaultPaymentBalanceDays: number;
  payDefaultPaymentDiscountDays: number;
  payDefaultPaymentDiscountPct: number;
}>;

export type SystemVarsLedgerDto = WithOptimisticLock<{
  gstApplies: boolean;
  gstPct: number;
  currentFinYear: number;
  lastMonthFinYear: number;
  conversionDate: Date;
  profitLossLevel: number;
  balanceLevel: number;
  gstForIncome?: number;
}>;

export const XeroSyncType = createEnum('XERO_SYNC_TYPE', {
  OfficeWithoutXero: 'OFFICE_WO_XERO',
  NewOffice: 'NEW_OFFICE',
  OfficeWithXero: 'OFFICE_W_XERO',
});

export type XeroSyncType = EnumSet<typeof XeroSyncType>;

export type SystemVarsAccountXeroLastInfoDto = {
  xeroConversionDate: Date;
  xeroSyncLastBills: any;
  xeroSyncLastCC?: Date;
  xeroSyncLastJobs?: Date;
  xeroSyncLastContacts?: Date;
  xeroSyncLastInvoices?: Date;
  xeroSyncLastJournalNumber: number;
  xeroSyncLastOverpayments: any;
};

export type SystemVarsAccountsXeroOauth1Dto = WithPrivilegedToken<WithOptimisticLock<SystemVarsAccountXeroLastInfoDto & {
  xeroUserAgent: string;
  xeroConsumerKey: string;
  xeroConsumerSecret: string;
  xeroWebhookKey: string;
  xeroWebhookUrl: string;
  xeroSyncType: XeroSyncType;
}>>;

export type SystemVarsAccountsXeroDto = WithPrivilegedToken<WithOptimisticLock<SystemVarsAccountXeroLastInfoDto & {
  xeroCallbackUrl: string;
  xeroClientId: string;
  xeroClientSecret: string;
  organizationName: string;
  financialYears?: string[];
  selectedFinancialYear: number;
  xeroWebhookKey: string;
  xeroWebhookUrl: string;
  xeroSyncType: XeroSyncType;
  etag?: string;
  requiresReconsent?: boolean;
}>>;

export type SystemVarsAccountsXeroUrlDto = {
  url: string;
};

export type SystemVarsAccountsXeroSyncTypeDto = {
  xeroSyncType: XeroSyncType;
};

export type SystemVarsAccountsXeroSyncDto = {
  syncCostCenters: boolean;
  syncJobNumber: boolean;
  syncContacts: boolean;
  syncInvoices: boolean;
};

export type SystemVarsAccountingReceivableDto = WithPrivilegedToken<WithOptimisticLock<{
  incomeAccountContractJob: AccountingAccountDto;
  incomeAccountSpecJob: AccountingAccountDto;
  wipAssetAccount: AccountingAccountDto;
  wipOpening: AccountingAccountDto;
  wipClosing: AccountingAccountDto;
}>>;

export type SystemVarsAccountingJobsDto = WithPrivilegedToken<WithOptimisticLock<{
  jobAccounts: AccountingAccountDto[];
}>>;

export type SystemVarsAccountingExpenseDto = WithPrivilegedToken<WithOptimisticLock<{
  jobExpenseAccounts: AccountingAccountDto[];
}>>;

export enum SystemVarsAccountingJobs {
  'OVERHEADS' = 'overheads',
  'EXPENSE' = 'expense',
  'ADVERTISE' = 'advertise',
  'WAGES' = 'wages',
  'LIABILITIES' = 'liabilities',
}

export type AccountingMigrationDataDto = {
  accounts: AccountingMigrationAccountDto[];
  gstCategories: AccountingMigrationGstCategoryDto[];
};

export type AccountingMigrationAccountDto = {
  accountId: ID;
  code: string;
  name: string;
  externalId?: ID;
  externalCode?: string;
  externalName?: string;
  isMandatory: boolean;
  isBankAccount: boolean;
};

export type AccountingMigrationGstCategoryDto = {
  gstCategoryId: ID;
  code: string;
  name: string;
  ratePct?: number;
  taxType?: ID;
  taxName?: string;
  taxRate?: number;
};

export type XeroAccountCreateRequestDto = {
  code: string;
  name: string;
  type: string;
  enablePaymentsToAccount: boolean;
};

export type XeroSyncProgressDto = {
  syncInProgress: boolean;
  initialSyncInProgress: boolean;
  initialSyncFailed: boolean;
};

export const EMPTY_XERO_SYNC_PROGRESS: SystemVarsAccountsXeroSyncProgress = {
  syncInProgress: false,
  initialSyncInProgress: false,
  initialSyncFailed: false,
};
